import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import logo from "assets/images/logo.png";
import { ROUTES } from "constants/index";
import { Button } from "components/ui";
import PageTransitionLoader from "components/ui/PageTransitionLoader";
import { useAuth } from "hooks/useAuth";

interface MarketingLayoutProps {
  children: React.ReactNode;
}

const navItems = [
  { to: ROUTES.FEATURES, label: "Features" },
  { to: ROUTES.CONTACT, label: "Contact Us" },
  { to: ROUTES.PRICING, label: "Pricing" },
  { to: ROUTES.PRIVACY, label: "Privacy" },
  { to: ROUTES.DATA_POLICY, label: "Data Policy" },
  { to: ROUTES.TERMS, label: "Terms" },
];
const getNavItemsMobile = (showLoggedInState: boolean, isAuthenticated: boolean): { to: string; label: string }[] => {
  const baseItems: { to: string; label: string }[] = [
    { to: ROUTES.FEATURES, label: "Features" },
    { to: ROUTES.PRICING, label: "Pricing" },
    { to: ROUTES.CONTACT, label: "Contact Us" },
    { to: ROUTES.PRIVACY, label: "Privacy" },
    { to: ROUTES.DATA_POLICY, label: "Data Policy" },
    { to: ROUTES.TERMS, label: "Terms" },
  ];

  if (showLoggedInState) {
    const items = [...baseItems];
    if (isAuthenticated) items.push({ to: ROUTES.DASHBOARD, label: "Go to Backoffice" });
    return items;
  }

  return [
    ...baseItems,
    { to: ROUTES.LOGIN, label: "Sign in" },
    { to: ROUTES.PRICING, label: "Get started" },
  ];
};

export const MarketingLayout: React.FC<MarketingLayoutProps> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const showLoggedInState = isAuthenticated || user != null;
  const displayName = (user?.name || user?.username || (user as any)?.email || "").trim() || "Account";

  const handleLogout = async () => {
    await logout();
    if (location.pathname !== ROUTES.HOME) navigate(ROUTES.HOME);
  };

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      el?.scrollIntoView({ behavior: "smooth" });
    }
  }, [location]);
  return (
    <div className="min-h-screen flex flex-col bg-marketing">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:bg-white focus:text-secondary-900 focus:border focus:border-secondary-300 focus:rounded-md focus:px-3 focus:py-2"
      >
        Skip to content
      </a>
      <header className="sticky top-0 z-40 border-b border-secondary-200 bg-white shadow-[0_1px_0_0_rgba(0,0,0,0.03)] dark:bg-secondary-900/80 dark:border-secondary-700/50 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="relative flex min-h-[4.5rem] items-center justify-between gap-4 py-2">
            <Link
              to={ROUTES.HOME}
              className="flex shrink-0 items-center gap-3 no-underline group"
            >
              <img
                src={logo}
                alt="YourQCard"
                className="h-12 w-12 object-contain sm:h-14 sm:w-14"
              />
              <div className="flex flex-col">
                <span className="font-display text-lg font-semibold tracking-tight text-primary-600 group-hover:text-primary-700 sm:text-xl">
                  YourQCard
                </span>
                <span className="text-xs font-medium text-secondary-500 sm:text-[13px]">
                  Your Digital Business Card
                </span>
              </div>
            </Link>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden md:block">
              <nav className="flex items-center gap-8" aria-label="Main navigation">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `relative py-1 text-sm font-medium transition-colors duration-200 after:absolute after:left-0 after:bottom-0 after:h-0.5 after:rounded-full after:bg-primary-500 after:transition-all duration-200 ${
                        isActive
                          ? "text-primary-600 after:w-full"
                          : "text-secondary-600 hover:text-secondary-900 after:w-0 hover:after:w-full"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            </div>
            <div className="hidden md:flex items-center gap-3">
              {showLoggedInState ? (
                <>
                  <span
                    className="hidden sm:block text-sm font-medium text-secondary-600 truncate max-w-[140px]"
                    title={displayName}
                  >
                    {displayName}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="rounded-lg border border-secondary-200 bg-white px-4 py-2 text-secondary-700 hover:bg-secondary-50 hover:border-secondary-300 hover:text-secondary-900 dark:bg-secondary-800/50 dark:border-secondary-600 dark:hover:bg-secondary-700/50"
                  >
                    Logout
                  </Button>
                  {isAuthenticated && (
                    <Button
                      size="sm"
                      onClick={() => navigate(ROUTES.DASHBOARD)}
                      className="rounded-lg px-4 py-2 bg-primary-600 text-white shadow-sm hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
                    >
                      Go to Backoffice
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <Link to={ROUTES.LOGIN}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-lg border border-secondary-200 bg-white px-4 py-2 text-secondary-700 hover:bg-secondary-50 hover:border-secondary-300 dark:bg-secondary-800/50 dark:hover:bg-secondary-700/50"
                    >
                      Sign in
                    </Button>
                  </Link>
                  <Link to={ROUTES.PRICING}>
                    <Button
                      size="sm"
                      className="rounded-lg px-4 py-2 bg-primary-600 text-white shadow-sm hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
                    >
                      Get started
                    </Button>
                  </Link>
                </>
              )}
            </div>
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center p-2.5 rounded-lg border border-secondary-200 bg-white hover:bg-secondary-50 text-secondary-700"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-label="Toggle menu"
            >
              <svg
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden border-t border-secondary-200 bg-white dark:bg-secondary-900/90 backdrop-blur-sm">
            <div className="px-4 py-3 space-y-1">
              {showLoggedInState && (
                <div className="flex items-center justify-between px-2 py-2 border-b border-secondary-200">
                  <span className="text-sm font-medium text-secondary-800 truncate max-w-[180px]" title={displayName}>
                    {displayName}
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => { setOpen(false); handleLogout(); }} className="text-secondary-700">
                    Logout
                  </Button>
                </div>
              )}
              {getNavItemsMobile(showLoggedInState, isAuthenticated).map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `block px-2 py-2 rounded-md text-sm font-medium ${
                      isActive
                        ? "text-primary-700 bg-primary-50"
                        : "text-secondary-800 hover:bg-secondary-100"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </header>

      <main id="main-content" className="flex-1 overflow-x-hidden">
        <PageTransitionLoader />
        {children}
      </main>

      <footer className="border-t border-secondary-200/70 bg-white/60 backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-secondary-600">
              <Link to={ROUTES.HOME} className="flex items-center gap-2">
                <img src={logo} alt="Qcard" className="h-12 w-18" />
              </Link>
              <div className="text-center md:text-right">
                Powered by <span className="font-medium">IDS</span> ©{" "}
                {new Date().getFullYear()}
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:items-center md:gap-6 gap-2 text-sm text-secondary-600">
              <div className="flex flex-wrap items-center justify-center md:justify-end gap-4">
                <NavLink to={ROUTES.TERMS} className="hover:text-secondary-900">
                  Terms
                </NavLink>
                <span
                  className="mx-3 h-4 w-px bg-secondary-300"
                  aria-hidden="true"
                ></span>
                <NavLink
                  to={ROUTES.PRIVACY}
                  className="hover:text-secondary-900"
                >
                  Privacy
                </NavLink>
                <span
                  className="mx-3 h-4 w-px bg-secondary-300"
                  aria-hidden="true"
                ></span>
                <NavLink to={ROUTES.DATA_POLICY} className="hover:text-secondary-900">
                  Data Policy
                </NavLink>
                <span
                  className="mx-3 h-4 w-px bg-secondary-300"
                  aria-hidden="true"
                ></span>
                <NavLink
                  to={ROUTES.PRICING}
                  className="hover:text-secondary-900"
                >
                  Pricing
                </NavLink>
                <span
                  className="mx-3 h-4 w-px bg-secondary-300"
                  aria-hidden="true"
                ></span>
                <NavLink
                  to={ROUTES.CONTACT}
                  className="hover:text-secondary-900"
                >
                  Contact Us
                </NavLink>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MarketingLayout;
